/**
 * View enum + router. Same shape as the dashboard's `view-router.tsx`,
 * trimmed to the admin's P1 surface (ADR-0140):
 *
 *   - `login`    — magic-link request form (anti-enumeration).
 *   - `verify`   — handles `?token=…` callback URLs (the token's POSTed
 *                  back to /internal/admin/auth/callback).
 *   - `accounts` — list + detail + edit; the workhorse of P1.
 *   - `metrics`  — placeholder; backend not built yet.
 *   - `team`     — placeholder; backend not built yet.
 *
 * `setView` is plumbed through providers so the AuthProvider's
 * bootstrap-401 routing and the HTTP client's `on_unauthorized`
 * callback can both reach it. Same shape as the dashboard's
 * `app/providers.tsx`.
 */

import { useEffect, useState } from 'react';

import { AccountDetailView } from '../views/accounts/AccountDetailView.js';
import { AccountsListView } from '../views/accounts/AccountsListView.js';
import { HealthView } from '../views/health/HealthView.js';
import { LoginView } from '../views/login/LoginView.js';
import { VerifyView } from '../views/login/VerifyView.js';
import { MetricsView } from '../views/metrics/MetricsView.js';
import { PlaceholderView } from '../views/placeholder/PlaceholderView.js';
import { AddonEditView } from '../views/plans/AddonEditView.js';
import { PlanEditView } from '../views/plans/PlanEditView.js';
import { PlansView } from '../views/plans/PlansView.js';
import { SmokeRunsView } from '../views/smoke/SmokeRunsView.js';
import { ProtocolTestsView } from '../views/protocol-tests/ProtocolTestsView.js';
import { PurgeView } from '../views/purge/PurgeView.js';
import { DeletionsView } from '../views/deletions/DeletionsView.js';
import { JobsView } from '../views/jobs/JobsView.js';
import { LeadsView } from '../views/leads/LeadsView.js';
import { TeamView } from '../views/team/TeamView.js';
import { useAuth } from './auth-context.js';
import { canView } from '../lib/roles.js';
import { StaffLogView } from '../views/staff-log/StaffLogView.js';
import { TestSuiteView } from '../views/testsuite/TestSuiteView.js';
import { TestKitsView } from '../views/test-kits/TestKitsView.js';
import { SettingsView } from '../views/settings/SettingsView.js';
import { AgentRegistryView } from '../views/agent-registry/AgentRegistryView.js';
import { TrustEngineView } from '../views/trust-engine/TrustEngineView.js';
import { TrustDisputesView } from '../views/trust-disputes/TrustDisputesView.js';
import { ExperimentsView } from '../views/experiments/ExperimentsView.js';
import { AgentOperatorsView } from '../views/agent-operators/AgentOperatorsView.js';
import { InstallFleetView } from '../views/install-fleet/InstallFleetView.js';
import { VerifiedCrawlersView } from '../views/verified-crawlers/VerifiedCrawlersView.js';
import { AttackPathDecisionsView } from '../views/attack-path-decisions/AttackPathDecisionsView.js';
// Founder request 2026-10-29 — ops2026 Agencies panel.
import { AgenciesView } from '../views/agencies/AgenciesView.js';

export type View =
  | 'login'
  | 'verify'
  | 'accounts'
  | 'account-detail'
  | 'plans'
  | 'plan-detail'
  | 'plan-new'
  // ADR-0263 S1 — add-on catalog editor + create flow. Lives under
  // the Plans tab strip, not a new nav entry.
  | 'addon-detail'
  | 'addon-new'
  | 'metrics'
  | 'health'
  | 'testsuite'
  // Testing area front page (founder request 2026-10-31).
  | 'test-kits'
  // ADR-0176 §12 — account-scoped agent smoke-test harness board.
  | 'smoke'
  // ADR-0180 slice 2b — purge-run approval board.
  | 'purge'
  // ADR-0180 slice 3c-2 — account-deletion approval board.
  | 'deletions'
  | 'jobs'
  | 'leads'
  | 'staff-log'
  | 'team'
  // ADR-0185 §2.4 — platform settings (signup breaker threshold).
  | 'settings'
  // ADR-0229 — agent-registry discovery proposals + approvals.
  | 'agent-registry'
  // ADR-0231 — Trust engine slice 1, shadow-only read-only panel.
  | 'trust-engine'
  // ADR-0235 — merchant trust-label disputes queue.
  | 'trust-disputes'
  // ADR-0233 — Live homepage experiment on sill.so (variants A/B/C).
  | 'experiments'
  // ADR-0234 — agent operator (AI company) enrollment review.
  | 'agent-operators'
  // ADR-0239 — install fleet redeploy (ops-only).
  | 'install-fleet'
  // ADR-0245 §4.4.2 — verified-crawler review (approve/reject pending
  // IP-range snapshots the daily refresh job proposes).
  | 'verified-crawlers'
  // ADR-0255 — ops2026 Protocol Test Agents.
  | 'protocol-tests'
  | 'attack-path-decisions'
  // Founder request 2026-10-29 — ops2026 Agencies panel.
  | 'agencies';

export interface ViewOptions {
  /** Account ID to land on when switching to `account-detail`. */
  readonly accountId?: string;
  /** Plan ID to land on when switching to `plan-detail`. */
  readonly planId?: string;
  /** ADR-0263 S1 — add-on ID to land on when switching to `addon-detail`. */
  readonly addonId?: string;
  /** Smoke run to auto-open when switching to `smoke` (e.g. right after
   *  starting one from the account detail view). */
  readonly smokeRunId?: string;
}

export type SetView = (v: View, opts?: ViewOptions) => void;

export interface ViewRouterProps {
  view: View;
  setView: SetView;
  /** Set when `view === 'account-detail'`. */
  accountId: string | null;
  /** Set when `view === 'plan-detail'`. */
  planId: string | null;
  /** ADR-0263 S1 — set when `view === 'addon-detail'`. */
  addonId: string | null;
  /** Set when switching to `smoke` with a run to auto-open. */
  smokeRunId: string | null;
}

export function ViewRouter({
  view,
  setView,
  accountId,
  planId,
  addonId,
  smokeRunId,
}: ViewRouterProps): JSX.Element {
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;
  if (auth.state.kind === 'authenticated' && !canView(role, view)) {
    return (
      <PlaceholderView
        title="Not available for your role"
        kicker="Access"
        body="Your ops role does not include this page. Ask the owner if you need it."
      />
    );
  }
  switch (view) {
    case 'login':
      return <LoginView />;
    case 'verify':
      return <VerifyView setView={setView} />;
    case 'accounts':
      return <AccountsListView setView={setView} />;
    case 'account-detail':
      if (accountId === null) {
        // Defensive: should not happen — setView('account-detail') is
        // only called with an opts.accountId — but a stale URL deep
        // link could land here.
        return <AccountsListView setView={setView} />;
      }
      return <AccountDetailView accountId={accountId} setView={setView} />;
    case 'plans':
      return <PlansView setView={setView} />;
    case 'plan-new':
      // Editor in "create" mode — no plan_id yet; save POSTs.
      return <PlanEditView setView={setView} planId={null} />;
    case 'plan-detail':
      if (planId === null) {
        return <PlansView setView={setView} />;
      }
      return <PlanEditView setView={setView} planId={planId} />;
    case 'addon-new':
      return <AddonEditView setView={setView} addonId={null} />;
    case 'addon-detail':
      if (addonId === null) {
        return <PlansView setView={setView} />;
      }
      return <AddonEditView setView={setView} addonId={addonId} />;
    case 'metrics':
      return <MetricsView setView={setView} />;
    case 'health':
      return <HealthView />;
    case 'testsuite':
      return <TestSuiteView />;
    case 'test-kits':
      return <TestKitsView setView={setView} />;
    case 'smoke':
      return <SmokeRunsView initialRunId={smokeRunId} />;
    case 'protocol-tests':
      return <ProtocolTestsView />;
    case 'purge':
      return <PurgeView />;
    case 'deletions':
      return <DeletionsView />;
    case 'jobs':
      return <JobsView />;
    case 'leads':
      return <LeadsView setView={setView} />;
    case 'staff-log':
      return <StaffLogView setView={setView} />;
    case 'settings':
      return <SettingsView />;
    case 'team':
      return <TeamView />;
    case 'agent-registry':
      return <AgentRegistryView />;
    case 'trust-engine':
      return <TrustEngineView />;
    case 'trust-disputes':
      return <TrustDisputesView />;
    case 'experiments':
      return <ExperimentsView />;
    case 'agent-operators':
      return <AgentOperatorsView />;
    case 'install-fleet':
      return <InstallFleetView />;
    case 'verified-crawlers':
      return <VerifiedCrawlersView />;
    case 'attack-path-decisions':
      return <AttackPathDecisionsView />;
    case 'agencies':
      return <AgenciesView />;
    default: {
      // Exhaustiveness guard.
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

/** Views that live in the browser history and the URL. Auth views
 *  ('login' / 'verify') never do: Back must not return to a sign-in step. */
const HISTORY_VIEWS: ReadonlySet<View> = new Set<View>([
  'accounts', 'account-detail', 'plans', 'plan-detail', 'plan-new',
  // ADR-0263 S1 — add-on editor + create flow.
  'addon-detail', 'addon-new',
  'metrics',
  'health', 'testsuite', 'test-kits', 'smoke', 'protocol-tests', 'purge', 'deletions', 'jobs', 'leads', 'staff-log',
  'team', 'settings', 'agent-registry', 'trust-engine', 'trust-disputes', 'experiments', 'agent-operators',
  'install-fleet', 'verified-crawlers', 'attack-path-decisions',
  // Founder request 2026-10-29.
  'agencies',
] as View[]);

interface RouteState {
  readonly view: View;
  readonly accountId: string | null;
  readonly planId: string | null;
  readonly addonId: string | null;
  readonly smokeRunId: string | null;
}

const HISTORY_KEY = 'sill_ops_route';

function routeUrl(r: RouteState): string {
  const url = new URL(window.location.href);
  for (const k of ['view', 'account', 'plan', 'addon', 'run', 'token']) url.searchParams.delete(k);
  if (HISTORY_VIEWS.has(r.view)) {
    url.searchParams.set('view', r.view);
    if (r.accountId !== null) url.searchParams.set('account', r.accountId);
    if (r.planId !== null) url.searchParams.set('plan', r.planId);
    if (r.addonId !== null) url.searchParams.set('addon', r.addonId);
    if (r.smokeRunId !== null) url.searchParams.set('run', r.smokeRunId);
  }
  return url.pathname + url.search + url.hash;
}

/** Restore a route from the URL (deep link / reload). */
function routeFromUrl(initial: View): RouteState {
  const base: RouteState = {
    view: initial,
    accountId: null,
    planId: null,
    addonId: null,
    smokeRunId: null,
  };
  if (typeof window === 'undefined' || initial === 'verify') return base;
  const q = new URL(window.location.href).searchParams;
  const view = q.get('view') as View | null;
  const accountId = q.get('account');
  const planId = q.get('plan');
  const addonId = q.get('addon');
  const smokeRunId = q.get('run');
  if (view === 'account-detail' && accountId !== null) {
    return { view, accountId, planId: null, addonId: null, smokeRunId: null };
  }
  if (view === 'plan-detail' && planId !== null) {
    return { view, accountId: null, planId, addonId: null, smokeRunId: null };
  }
  if (view === 'addon-detail' && addonId !== null) {
    return { view, accountId: null, planId: null, addonId, smokeRunId: null };
  }
  if (view === 'smoke') {
    return { view, accountId: null, planId: null, addonId: null, smokeRunId };
  }
  return base;
}

function isRouteState(v: unknown): v is RouteState {
  return typeof v === 'object' && v !== null && typeof (v as RouteState).view === 'string';
}

/**
 * Hook returning the current route + `setView`. Hoisted out of <App /> so
 * the AuthProvider + HTTP client can call `setView('login')` uniformly.
 *
 * Browser history: every navigation to a HISTORY_VIEWS view pushes an
 * entry, so the browser Back button returns to the previous ops page
 * instead of leaving ops2026. popstate restores the stored route without
 * pushing. Auth views replace the current entry instead of pushing.
 */
export function useViewState(initial: View = 'accounts'): {
  view: View;
  setView: SetView;
  accountId: string | null;
  planId: string | null;
  addonId: string | null;
  smokeRunId: string | null;
} {
  const [route, setRoute] = useState<RouteState>(() => routeFromUrl(initial));

  // Seed the first history entry with the starting route so Back to it
  // restores state instead of reloading.
  useEffect(() => {
    if (typeof window === 'undefined' || route.view === 'verify') return;
    if (!isRouteState((window.history.state as Record<string, unknown> | null)?.[HISTORY_KEY])) {
      window.history.replaceState({ [HISTORY_KEY]: route }, '', routeUrl(route));
    }
    const onPop = (e: PopStateEvent): void => {
      const stored = (e.state as Record<string, unknown> | null)?.[HISTORY_KEY];
      setRoute(isRouteState(stored) ? stored : routeFromUrl('accounts'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const setView: SetView = (v, opts) => {
    const next: RouteState = {
      view: v,
      accountId: opts?.accountId ?? null,
      planId: opts?.planId ?? null,
      addonId: opts?.addonId ?? null,
      smokeRunId: opts?.smokeRunId ?? null,
    };
    setRoute(next);
    if (typeof window !== 'undefined') {
      const current = (window.history.state as Record<string, unknown> | null)?.[HISTORY_KEY];
      const same =
        isRouteState(current)
        && current.view === next.view
        && current.accountId === next.accountId
        && current.planId === next.planId
        && current.addonId === next.addonId
        && current.smokeRunId === next.smokeRunId;
      if (HISTORY_VIEWS.has(v) && !same) {
        window.history.pushState({ [HISTORY_KEY]: next }, '', routeUrl(next));
      } else if (!HISTORY_VIEWS.has(v)) {
        window.history.replaceState({ [HISTORY_KEY]: next }, '', routeUrl(next));
      }
      window.scrollTo(0, 0);
    }
  };

  return {
    view: route.view,
    setView,
    accountId: route.accountId,
    planId: route.planId,
    addonId: route.addonId,
    smokeRunId: route.smokeRunId,
  };
}
